import { env } from '@/config/env.ts'
import type { TestCase, TestResult } from './testCases.ts'

async function runStress(fn: () => Promise<void>): Promise<TestResult> {
  const start = performance.now()
  try {
    await fn()
    return { pass: true, duration: performance.now() - start }
  } catch (e) {
    return {
      pass: false,
      error: e instanceof Error ? e.message : String(e),
      duration: performance.now() - start,
    }
  }
}

function assert(condition: boolean, message: string) {
  if (!condition) throw new Error(message)
}

// ---------------------------------------------------------------------------
// Concurrent API Stress
// ---------------------------------------------------------------------------

const concurrentApiStress: TestCase = {
  id: 'stress-concurrent-api',
  name: 'Concurrent API bombardment (20 parallel requests)',
  category: 'Stress Tests',
  run: () =>
    runStress(async () => {
      const endpoints: Array<{
        name: string
        url: string
        opts?: RequestInit
      }> = []

      if (env.supabaseUrl && env.supabaseAnonKey) {
        for (let i = 0; i < 10; i++) {
          endpoints.push({
            name: `supabase-${i}`,
            url: `${env.supabaseUrl}/rest/v1/`,
            opts: {
              method: 'HEAD',
              headers: { apikey: env.supabaseAnonKey },
            },
          })
        }
      }

      if (env.pinataGateway) {
        const gateway = env.pinataGateway.startsWith('http')
          ? env.pinataGateway
          : `https://${env.pinataGateway}`
        for (let i = 0; i < 10; i++) {
          endpoints.push({
            name: `pinata-${i}`,
            url: gateway,
            opts: { method: 'HEAD', mode: 'no-cors' },
          })
        }
      }

      assert(
        endpoints.length > 0,
        'No API endpoints configured for stress test',
      )

      const results = await Promise.allSettled(
        endpoints.map(async (ep) => {
          const res = await fetch(ep.url, ep.opts)
          return { name: ep.name, status: res.status }
        }),
      )

      const failures = results.filter((r) => r.status === 'rejected')
      assert(
        failures.length === 0,
        `${failures.length}/${results.length} requests failed: ${failures
          .map((f) => (f as PromiseRejectedResult).reason)
          .join(', ')}`,
      )
    }),
}

// ---------------------------------------------------------------------------
// Encryption Round-Trip Stress
// ---------------------------------------------------------------------------

const encryptionRoundTrip: TestCase = {
  id: 'stress-encryption-roundtrip',
  name: 'Encryption service instantiation stress (10 iterations)',
  category: 'Stress Tests',
  run: () =>
    runStress(async () => {
      const { createLitEncryptionService } =
        await import('@/services/encryption/lit.service.ts')

      for (let i = 0; i < 10; i++) {
        const svc = createLitEncryptionService()
        assert(
          typeof svc.connect === 'function',
          `Iteration ${i}: connect missing`,
        )
        assert(
          typeof svc.encryptString === 'function',
          `Iteration ${i}: encryptString missing`,
        )
        assert(svc.isReady() === false, `Iteration ${i}: should not be ready`)
      }
    }),
}

// ---------------------------------------------------------------------------
// Service Module Import Stress
// ---------------------------------------------------------------------------

const serviceImportStress: TestCase = {
  id: 'stress-service-imports',
  name: 'Rapid service module re-imports (20 iterations)',
  category: 'Stress Tests',
  run: () =>
    runStress(async () => {
      for (let i = 0; i < 20; i++) {
        const [enc, ipfs, contract, db, notif] = await Promise.all([
          import('@/services/encryption/index.ts'),
          import('@/services/ipfs/index.ts'),
          import('@/services/contract/index.ts'),
          import('@/services/database/index.ts'),
          import('@/services/notification/index.ts'),
        ])
        assert(!!enc, `Iteration ${i}: encryption import failed`)
        assert(!!ipfs, `Iteration ${i}: ipfs import failed`)
        assert(!!contract, `Iteration ${i}: contract import failed`)
        assert(!!db, `Iteration ${i}: database import failed`)
        assert(!!notif, `Iteration ${i}: notification import failed`)
      }
    }),
}

// ---------------------------------------------------------------------------
// DOM / Routing Stress
// ---------------------------------------------------------------------------

const routingStress: TestCase = {
  id: 'stress-routing',
  name: 'Rapid route fetches (30 requests)',
  category: 'Stress Tests',
  run: () =>
    runStress(async () => {
      const routes = [
        '/',
        '/dashboard',
        '/profile',
        '/settings',
        '/benefactor',
        '/beneficiary',
      ]

      const requests = []
      for (let i = 0; i < 30; i++) {
        const route = routes[i % routes.length]
        requests.push(fetch(route).then((r) => ({ route, ok: r.ok })))
      }

      const results = await Promise.all(requests)
      const failures = results.filter((r) => !r.ok)
      assert(
        failures.length === 0,
        `${failures.length} route fetches failed: ${failures.map((f) => f.route).join(', ')}`,
      )
    }),
}

// ---------------------------------------------------------------------------
// Memory Baseline Check
// ---------------------------------------------------------------------------

const memoryBaseline: TestCase = {
  id: 'stress-memory-baseline',
  name: 'Memory usage baseline check',
  category: 'Stress Tests',
  run: () =>
    runStress(async () => {
      const perf = performance as Performance & {
        memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number }
      }

      if (!perf.memory) {
        return // non-Chrome — skip silently
      }

      const before = perf.memory.usedJSHeapSize

      // Run a burst of work to detect leaks
      for (let i = 0; i < 50; i++) {
        await import('@/services/encryption/index.ts')
        await import('@/services/ipfs/index.ts')
        await import('@/services/contract/index.ts')
      }

      // Allow GC opportunity
      await new Promise((r) => setTimeout(r, 500))

      const after = perf.memory.usedJSHeapSize
      const growthPct = ((after - before) / before) * 100

      assert(
        growthPct < 50,
        `Memory grew ${growthPct.toFixed(1)}% (${(before / 1024 / 1024).toFixed(1)}MB → ${(after / 1024 / 1024).toFixed(1)}MB). Possible leak.`,
      )
    }),
}

// ---------------------------------------------------------------------------
// Rapid Supabase Read Stress
// ---------------------------------------------------------------------------

const supabaseReadStress: TestCase = {
  id: 'stress-supabase-reads',
  name: 'Supabase rapid read stress (15 parallel)',
  category: 'Stress Tests',
  run: () =>
    runStress(async () => {
      assert(!!env.supabaseUrl, 'Supabase URL not configured')

      const requests = Array.from({ length: 15 }, (_, i) =>
        fetch(`${env.supabaseUrl}/rest/v1/`, {
          method: 'HEAD',
          headers: { apikey: env.supabaseAnonKey },
        }).then((r) => ({ i, status: r.status })),
      )

      const results = await Promise.allSettled(requests)
      const rejected = results.filter((r) => r.status === 'rejected')
      assert(
        rejected.length === 0,
        `${rejected.length}/15 Supabase reads failed`,
      )

      const fulfilled = results
        .filter(
          (r): r is PromiseFulfilledResult<{ i: number; status: number }> =>
            r.status === 'fulfilled',
        )
        .map((r) => r.value)
      const serverErrors = fulfilled.filter((r) => r.status >= 500)
      assert(
        serverErrors.length === 0,
        `${serverErrors.length} Supabase 5xx errors`,
      )
    }),
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const stressTestCases: TestCase[] = [
  concurrentApiStress,
  encryptionRoundTrip,
  serviceImportStress,
  routingStress,
  memoryBaseline,
  supabaseReadStress,
]
